/**
 * The pure rules of the shell's open set, which the atoms and the open plan share.
 */

import type { Location } from 'react-router-dom';
import { NavigationType } from 'react-router-dom';

import type { OpenPanel, PanelId } from '@/types/panels';
import { PanelMode } from '@/types/panels';

/** The part of an open panel that deriveMode reads. */
type ModedPanel = { config?: { modes?: readonly PanelMode[] } } | null | undefined;

/** Settings while a settings-only panel is open, so the answer does not depend on the order. */
export function deriveMode(openPanels: readonly ModedPanel[]): PanelMode {
    for (const panel of openPanels) {
        const modes = panel?.config?.modes ?? [];
        if (modes.length === 1 && modes[0] === PanelMode.Settings) {
            return PanelMode.Settings;
        }
    }
    return PanelMode.View;
}

/**
 * Where a close of `id` goes back to: the last panel of `focusHistory` that stays in the row,
 * else the nearest neighbour, left first, because a list is on the left of its detail.
 */
export function getFocusBehind(
    rowIds: readonly PanelId[], focusHistory: readonly PanelId[], id: PanelId,
    closing: ReadonlySet<PanelId>,
): PanelId | null {
    const at = rowIds.indexOf(id);
    if (at < 0) {
        return null;
    }
    const stayingPanelIds = new Set(rowIds.filter((pid) => !closing.has(pid)));
    const stays = (pid: PanelId): boolean => stayingPanelIds.has(pid);
    return [...focusHistory].reverse().find(stays) ?? rowIds.slice(0, at).reverse().find(stays) ??
        rowIds.slice(at).find(stays) ?? null;
}

/** Whether a panel takes room in the row. On a phone, the chat drawer is a panel too. */
export function onStage(panel: Pick<OpenPanel, 'config'>, isPhone = false): boolean {
    return isPhone || !panel.config.isOffStage;
}

/**
 * The panels whose work a Back or Forward takes, or each panel for another path. The shell
 * asked the guards for its own navigations already.
 */
export function getHistoryDepartures(
    openPanels: readonly OpenPanel[], currentLocation: Location, nextLocation: Location,
    historyAction: NavigationType,
): PanelId[] {
    if (currentLocation.pathname !== nextLocation.pathname) {
        return openPanels.map((panel) => panel.id);
    }
    if (historyAction !== NavigationType.Pop) {
        return [];
    }
    const currentParams = new URLSearchParams(currentLocation.search);
    const nextParams = new URLSearchParams(nextLocation.search);
    const nextPanelIds = nextParams.get('panels')?.split(',') ?? [];
    return openPanels.filter((panel) => !nextPanelIds.includes(panel.id) ||
        panel.config.subjectProps.some((key) => currentParams.get(key) !== nextParams.get(key)))
        .map((panel) => panel.id);
}
